<template>
  <div
    v-if="show && feature"
    class="ags-hover-tooltip"
    :style="tooltipStyle"
  >
    <div class="tooltip-content">
      <div class="tooltip-title">Geographic Unit: {{ feature.properties.name || getFeatureDisplayName() }}</div>
      
      <!-- AGS Variable Values -->
      <div v-if="selectedVariables.length === 1 && feature.properties.ags_value !== null && feature.properties.ags_value !== undefined">
        <div class="tooltip-data">
          <span class="tooltip-label">{{ selectedVariables[0].label }}:</span>
          <span class="tooltip-value">{{ formatAgsVariableValue(feature.properties.ags_value, selectedVariables[0].format) }}</span>
        </div>
      </div>
      <div v-else-if="selectedVariables.length > 1 && feature.properties.ags_data">
        <div v-for="variable in selectedVariables" :key="variable.path" class="tooltip-data">
          <span class="tooltip-label">{{ variable.label }}:</span>
          <span class="tooltip-value">{{ getAgsVariableValue(feature.properties.ags_data, variable) }}</span>
        </div>
      </div>
      <div v-else-if="selectedVariables.length > 0" class="tooltip-data">
        <span class="tooltip-label">AGS Data:</span>
        <span class="tooltip-value">{{ selectedVariables.length }} variable{{ selectedVariables.length !== 1 ? 's' : '' }}</span>
      </div>
      
      <!-- Crime Data Info -->
      <div v-if="crimeLayerEnabled && feature.properties.crime_data" class="tooltip-data">
        <span class="tooltip-label">Risk Level:</span>
        <span 
          class="tooltip-value"
          :class="getRiskBadgeClass(feature.properties.crime_data.risk_level)"
        >
          {{ formatRiskLevel(feature.properties.crime_data.risk_level) }}
        </span>
      </div>
      
      <!-- Population/Household Data -->
      <div v-if="feature.properties.household_count" class="tooltip-data">
        <span class="tooltip-label">Households:</span>
        <span class="tooltip-value">{{ formatNumber(feature.properties.household_count) }}</span>
      </div>
      <div v-else-if="feature.properties.population" class="tooltip-data">
        <span class="tooltip-label">Population:</span>
        <span class="tooltip-value">{{ formatNumber(feature.properties.population) }}</span>
      </div>
      
      <div class="tooltip-hint">Click for details</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AgsHoverTooltip',
  
  props: {
    show: {
      type: Boolean,
      default: false
    },
    feature: {
      type: Object,
      default: null
    },
    tooltipStyle: {
      type: Object,
      default: () => ({})
    },
    selectedVariables: {
      type: Array,
      default: () => []
    },
    crimeLayerEnabled: {
      type: Boolean,
      default: false
    },
    layerType: {
      type: String,
      default: 'states'
    }
  },
  
  methods: {
    getFeatureDisplayName() {
      if (!this.feature || !this.feature.properties) {
        return 'Unknown';
      }
      
      const props = this.feature.properties;
      const code = props.geoid || props.code || '';
      
      if (this.layerType === 'states') {
        return `State ${code}`;
      } else if (this.layerType === 'counties') {
        return `County ${code}`;
      } else if (this.layerType === 'zips') {
        return `ZIP ${code}`;
      }
      
      return code;
    },
    
    getAgsVariableValue(agsData, variable) {
      if (!agsData) return 'N/A';
      
      // Access data using category_name as key: agsData[category_name].extracted_values[variable_path]
      const categoryData = agsData[variable.category_name];
      
      if (categoryData && categoryData.extracted_values) {
        const value = categoryData.extracted_values[variable.path];
        if (value !== undefined && value !== null) {
          return this.formatAgsVariableValue(value, variable.format);
        }
      }
      
      // Fallback to flat structure (single variable case)
      const value = agsData[variable.path];
      if (value !== undefined && value !== null) {
        return this.formatAgsVariableValue(value, variable.format);
      }
      
      return 'N/A';
    },
    
    formatAgsVariableValue(value, format) {
      if (value === null || value === undefined) return 'N/A';
      
      const num = parseFloat(value);
      if (isNaN(num)) return 'N/A';
      
      if (format === 'currency') {
        if (num >= 1000000) return '$' + (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return '$' + (num / 1000).toFixed(1) + 'k';
        return '$' + num.toLocaleString();
      } else if (format === 'percent') {
        return (num * 100).toFixed(1) + '%';
      } else {
        return this.formatNumber(num);
      }
    },
    
    formatNumber(value) {
      if (value === null || value === undefined) return 'N/A';
      
      const num = parseFloat(value);
      if (isNaN(num)) return 'N/A';
      
      if (num >= 1000000000) return (num / 1000000000).toFixed(1) + 'B';
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
      if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
      
      return num.toLocaleString();
    },
    
    formatRiskLevel(riskLevel) {
      if (!riskLevel) return 'N/A';
      
      const levels = {
        1: 'Very Low', 2: 'Low', 3: 'Moderate', 4: 'High', 5: 'Very High',
        'very_low': 'Very Low', 'low': 'Low', 'moderate': 'Moderate',
        'high': 'High', 'very_high': 'Very High'
      };
      
      return levels[riskLevel] || riskLevel;
    },
    
    getRiskBadgeClass(riskLevel) {
      const classes = {
        1: 'bg-green-100', 2: 'bg-blue-100', 3: 'bg-yellow-100',
        4: 'bg-orange-100', 5: 'bg-red-100'
      };
      return classes[riskLevel] || '';
    }
  }
};
</script>
